/**
 * 🔌 SignalR Context & Provider - TAM MÜKƏMMƏL VERSİYA
 * 
 * Fayl yeri: src/components/services/SignalRContext.jsx
 * 
 * Bütün SignalR hook-ları bu context vasitəsilə istifadə olunur.
 * 
 * @version 5.0.0
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import signalRService, { SignalREvents, ConnectionState } from './SignalRService';

const SignalRContext = createContext(null);

/**
 * SignalR Provider Component
 */
export function SignalRProvider({ children }) {
    const [auctionConnectionState, setAuctionConnectionState] = useState(ConnectionState.DISCONNECTED);
    const [notificationConnectionState, setNotificationConnectionState] = useState(ConnectionState.DISCONNECTED);
    const [isInitialized, setIsInitialized] = useState(false);
    
    const hasInitialized = useRef(false);

    // ═══════════════════════════════════════════════════════════════
    // 🚀 INITIALIZATION
    // ═══════════════════════════════════════════════════════════════
    useEffect(() => {
        if (hasInitialized.current) return;
        hasInitialized.current = true;

        const initialize = async () => {
            // AuctionHub-a avtomatik qoşul
            await signalRService.connectToAuctionHub();
            setAuctionConnectionState(signalRService.connectionState);
            setIsInitialized(true);
        };

        initialize();

        // State dəyişikliklərini dinlə
        const unsubscribe = signalRService.onStateChange((state) => {
            setAuctionConnectionState(state.auctionState);
            setNotificationConnectionState(state.notificationState);
        });

        // Cleanup
        return () => {
            unsubscribe();
        };
    }, []);

    // ═══════════════════════════════════════════════════════════════
    // 🎯 AUCTION HUB METHODS
    // ═══════════════════════════════════════════════════════════════

    /**
     * Hərrac otağına qoşul
     */
    const joinAuction = useCallback(async (auctionId) => {
        return signalRService.joinAuction(auctionId);
    }, []);

    /**
     * Hərrac otağından ayrıl
     */
    const leaveAuction = useCallback(async (auctionId) => {
        return signalRService.leaveAuction(auctionId);
    }, []);

    /**
     * 📈 Bid yeniləndikdə - Backend: UpdateBid(productId, userName, amount)
     * @param {Function} callback - (data) => void
     * @returns {Function} unsubscribe
     */
    const onBidUpdate = useCallback((callback) => {
        return signalRService.on(SignalREvents.UPDATE_BID, callback);
    }, []);

    /**
     * 😤 İstifadəçinin təklifi keçildikdə
     * @param {Function} callback - (data) => void
     * @returns {Function} unsubscribe
     */
    const onUserOutbid = useCallback((callback) => {
        return signalRService.on(SignalREvents.USER_OUTBID, callback);
    }, []);

    /**
     * 🏁 Hərrac bitdikdə
     * @param {Function} callback - (data) => void
     * @returns {Function} unsubscribe
     */
    const onAuctionEnded = useCallback((callback) => {
        return signalRService.on(SignalREvents.AUCTION_ENDED, callback);
    }, []);

    /**
     * ⏰ Hərrac uzadıldıqda
     * @param {Function} callback - (data) => void
     * @returns {Function} unsubscribe
     */
    const onAuctionExtended = useCallback((callback) => {
        return signalRService.on(SignalREvents.AUCTION_EXTENDED, callback);
    }, []);

    /**
     * 🏆 Hərrac qazanıldıqda
     * @param {Function} callback - (data) => void
     * @returns {Function} unsubscribe
     */
    const onAuctionWon = useCallback((callback) => {
        return signalRService.on(SignalREvents.AUCTION_WON, callback);
    }, []);

    // ═══════════════════════════════════════════════════════════════
    // 🔔 NOTIFICATION HUB METHODS
    // ═══════════════════════════════════════════════════════════════

    /**
     * NotificationHub-a qoşul
     */
    const connectNotifications = useCallback(async () => {
        const result = await signalRService.connectToNotificationHub();
        setNotificationConnectionState(signalRService.notificationConnectionState);
        return result;
    }, []);

    /**
     * 🔔 Yeni bildiriş gəldikdə
     * @param {Function} callback - (notification) => void
     * @returns {Function} unsubscribe
     */
    const onNotification = useCallback((callback) => {
        return signalRService.on(SignalREvents.RECEIVE_NOTIFICATION, callback);
    }, []);

    /**
     * 💰 Balans yeniləndikdə
     * @param {Function} callback - (data) => void
     * @returns {Function} unsubscribe
     */
    const onBalanceUpdated = useCallback((callback) => {
        return signalRService.on(SignalREvents.BALANCE_UPDATED, callback);
    }, []);

    /**
     * 🔢 Bildiriş sayı yeniləndikdə
     * @param {Function} callback - (data) => void
     * @returns {Function} unsubscribe
     */
    const onNotificationCountUpdate = useCallback((callback) => {
        return signalRService.on(SignalREvents.NOTIFICATION_COUNT, callback);
    }, []);

    /**
     * ✓ Bildirişi oxundu işarələ
     */
    const markAsRead = useCallback((notificationId) => {
        return signalRService.markNotificationAsRead(notificationId);
    }, []);

    /**
     * 🏓 Ping göndər
     */
    const ping = useCallback(() => {
        return signalRService.ping();
    }, []);

    // ═══════════════════════════════════════════════════════════════
    // 🔧 UTILITY METHODS
    // ═══════════════════════════════════════════════════════════════

    /**
     * Bağlantı statusları
     */
    const isAuctionConnected = auctionConnectionState === ConnectionState.CONNECTED;
    const isNotificationConnected = notificationConnectionState === ConnectionState.CONNECTED;

    /**
     * Status məlumatları
     */
    const getStatus = useCallback(() => signalRService.getStatus(), []);

    /**
     * Bağlantıları kəs
     */
    const disconnect = useCallback(() => signalRService.disconnect(), []);

    // ═══════════════════════════════════════════════════════════════
    // 📤 CONTEXT VALUE
    // ═══════════════════════════════════════════════════════════════

    const value = {
        // ═══════════════════════════════════════════════════════════
        // Connection States
        // ═══════════════════════════════════════════════════════════
        auctionConnectionState,
        notificationConnectionState,
        isAuctionConnected,
        isNotificationConnected,
        isInitialized,
        
        // ═══════════════════════════════════════════════════════════
        // Auction Methods
        // ═══════════════════════════════════════════════════════════
        joinAuction,
        leaveAuction,
        onBidUpdate,
        onUserOutbid,
        onAuctionEnded,
        onAuctionExtended,
        onAuctionWon,
        
        // ═══════════════════════════════════════════════════════════
        // Notification Methods
        // ═══════════════════════════════════════════════════════════
        connectNotifications,
        onNotification,
        onBalanceUpdated,
        onNotificationCountUpdate,
        markAsRead,
        ping,
        
        // ═══════════════════════════════════════════════════════════
        // Utility
        // ═══════════════════════════════════════════════════════════
        getStatus,
        disconnect,
        service: signalRService  // Raw service instance (advanced usage)
    };

    return (
        <SignalRContext.Provider value={value}>
            {children}
        </SignalRContext.Provider>
    );
}

/**
 * useSignalR Hook
 * 
 * @example
 * const { 
 *     joinAuction, 
 *     leaveAuction, 
 *     onBidUpdate,
 *     onUserOutbid,
 *     onAuctionEnded,
 *     onAuctionExtended,
 *     onAuctionWon,
 *     onBalanceUpdated,
 *     isAuctionConnected 
 * } = useSignalR();
 */
export function useSignalR() {
    const context = useContext(SignalRContext);
    
    if (!context) {
        throw new Error('useSignalR must be used within a SignalRProvider');
    }
    
    return context;
}

export default SignalRContext;